<template>
  <fieldset class="radio-group-fieldset">
    <legend v-if="label" class="col-12 multiselect-field-label radio-group-legend" :class="[labelClasses, headerCompactClass]">
      <span v-if="field.required" class="required">*</span>
      <span :class="{ 'multiselect-extended-title': hasExtendedHeader }" v-html="label"></span>
    </legend>
    <div v-if="field.description" class="multiselect-field-description">{{ field.description }}</div>
    <div v-if="field.disclaimer" class="multiselect-field-disclaimer">{{ field.disclaimer }}</div>
    <div class="multiselect-validation-root" v-bind="multiselectValidationAttrs" :aria-describedby="multiselectDescribedBy" :aria-invalid="multiselectAriaInvalid">
      <div
        class="checkbox-columns"
        :class="{ 'one-column': useOneColumnLayout, 'many-items': useManyItemsLayout }"
        data-toggle="buttons"
      >
        <div
          v-for="(option, idx) in options"
          :key="idx"
          class="multiselect-option-wrap"
        >
          <label
            :for="fieldId(idx)"
            class="justify-content-right checkbox-option-label"
            :class="{ 'checkbox-option-rich': hasOptionDetail(option) }"
          >
            <input
              class="red-checkbox"
              type="checkbox"
              :id="fieldId(idx)"
              :name="dataName()"
              autocomplete="off"
              :value="optionValue(option)"
              v-model="itemChecked[idx]"
              :disabled="isOptionDisabled(idx)"
              :aria-required="idx === 0 && field.required ? 'true' : undefined"
              @change="onOptionToggle(idx)"
            />
            <span v-if="hasOptionDetail(option)" class="checkbox-option-text checkbox-option-detail">
              <span v-if="option.title" class="multiselect-option-title">{{ option.title }}</span>
              <span v-else v-html="optionLabel(option)"></span>
              <span v-if="option.description" class="multiselect-option-description">{{ option.description }}</span>
            </span>
            <span v-else class="checkbox-option-text" v-html="optionLabel(option)"></span>
          </label>
          <div
            v-if="showNestedFields(option, idx)"
            v-show="itemChecked[idx]"
            class="nested-multiselect-fields"
          >
            <FormFieldComponent
              v-for="(subfield, fidx) in nestedFieldsFor(option)"
              :key="fidx"
              :field="customizeNestedField(subfield)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="field.required"
        class="invalid-feedback"
        :id="multiselectInvalidId"
      >{{ field.error_message }}</div>
    </div>
  </fieldset>
</template>

<script>
export default {
  props: {
    label: String,
    type: String,
    name: String,
    options: Array,
    subfield: Object,
    field: {
      type: Object,
      default:  () => ({})
    }
  },
  data() {
    return {
      itemChecked: [],
    };
  },
  computed: {
    labelClasses() {
      const classes = [];
      if (this.field?.bold_label) {
        classes.push("bold");
      }
      return classes;
    },
    multiselectInvalidId() {
      return this.dataName() + "invalid";
    },
    multiselectHasError() {
      return !!(this.field?.error_message);
    },
    multiselectDescribedBy() {
      if (this.multiselectHasError) {
        return this.multiselectInvalidId;
      }
      return undefined;
    },
    multiselectAriaInvalid() {
      return this.multiselectHasError ? 'true' : undefined;
    },
    multiselectValidationAttrs() {
      if (!this.field?.required) {
        return {};
      }
      return {
        "data-multiselect-at-least-one": "1",
        "data-multiselect-checkbox-name": this.dataName(),
        "data-multiselect-invalid-id": this.multiselectInvalidId
      };
    },
    useOneColumnLayout() {
      return !!this.field?.one_column;
    },
    hasExtendedHeader() {
      return !!(this.field?.description || this.field?.disclaimer);
    },
    headerCompactClass() {
      return this.hasExtendedHeader ? "multiselect-field-label-extended" : "";
    },
    selectionLimit() {
      const max = this.field?.max_selections;
      if (max == null || max === "") return null;
      const limit = Number(max);
      if (!Number.isFinite(limit) || limit < 1) return null;
      return limit;
    },
    checkedCount() {
      let count = 0;
      const items = this.itemChecked;
      for (let i = 0; i < items.length; i++) {
        if (items[i]) count++;
      }
      return count;
    },
    omitHiddenNestedFields() {
      if (this.hasExtendedHeader) return true;
      if (this.selectionLimit != null) return true;
      const options = this.options || [];
      for (let i = 0; i < options.length; i++) {
        if (this.hasOptionDetail(options[i])) return true;
      }
      return false;
    },
    useManyItemsLayout() {
      return this.$isDesktop() && this.options.length > 10 && !this.hasOpenNestedField && !this.useOneColumnLayout;
    },
    hasOpenNestedField() {
      if (!this.options || !this.options.length) return false;
      return this.options.some((option, idx) => this.hasNestedFields(option) && !!this.itemChecked[idx]);
    }
  },
  mounted() {
    this.itemChecked.length = this.$props.options.length;
  },
  methods: {
    colsize(option) {
      if (this.hasNestedFields(option)) return "col-12";
      if (this.$isDesktop()) {
        return "col-" + parseInt(12 / this.options.length);
      }
      return "col-6";
    },
    optionValue(option) {
      if (option == null) return "";
      if (Object.prototype.hasOwnProperty.call(option, "val")) return option.val;
      if (Object.prototype.hasOwnProperty.call(option, "text")) return option.text;
      return "";
    },
    optionLabel(option) {
      if (option == null) return "";
      if (option.label != null && option.label !== "") return option.label;
      if (option.text != null && typeof option.text === "string") return option.text;
      return String(this.optionValue(option));
    },
    nestedFieldsFor(option) {
      if (!option) return [];
      if (Array.isArray(option.fields) && option.fields.length) return option.fields;
      if (option.field) return [option.field];
      return [];
    },
    hasNestedFields(option) {
      return this.nestedFieldsFor(option).length > 0;
    },
    hasOptionDetail(option) {
      if (!option) return false;
      if (option.title != null && option.title !== "") return true;
      if (option.description != null && option.description !== "") return true;
      return false;
    },
    showNestedFields(option, idx) {
      if (!this.hasNestedFields(option)) return false;
      if (this.omitHiddenNestedFields && !this.itemChecked[idx]) return false;
      return true;
    },
    isOptionDisabled(idx) {
      const limit = this.selectionLimit;
      if (limit == null) return false;
      if (this.itemChecked[idx]) return false;
      return this.checkedCount >= limit;
    },
    customizeNestedField(field) {
      return field;
    },
    onOptionToggle(idx) {
      const limit = this.selectionLimit;
      if (limit != null && this.itemChecked[idx] && this.checkedCount > limit) {
        this.itemChecked[idx] = false;
      }
      this.$nextTick(() => {
        if (!this.itemChecked[idx]) {
          this.clearNestedInputsForOption(idx);
        }
        if (this.field?.required) {
          const any = this.itemChecked.some((c) => !!c);
          const inv = document.getElementById(this.multiselectInvalidId);
          if (inv && any) {
            inv.classList.remove("show");
          }
        }
      });
    },
    clearNestedInputsForOption(idx) {
      const form = this.$el && this.$el.closest ? this.$el.closest("form") : null;
      const root = form || document;
      const option = this.options[idx];
      const fields = this.nestedFieldsFor(option);
      for (let i = 0; i < fields.length; i++) {
        const dataName = fields[i].data_name;
        if (!dataName) continue;
        root.querySelectorAll("input, textarea").forEach((el) => {
          if (el.name !== dataName) return;
          const t = (el.type || "").toLowerCase();
          if (t === "hidden" || t === "checkbox" || t === "radio") return;
          el.value = "";
        });
      }
    },
    dataName() {
      return (this.name || "data_name_undefined") + "[]";
    },
    fieldId(idx) {
      // Generate a unique ID by replacing invalid characters in data_name
      const baseId = (this.name || "data_name_undefined")
        .replace(/\[|\]/g, "_") // Replace [ and ] with _
        // .replace(/_+/g, "_") // Replace multiple underscores with a single underscore
        .replace(/^_|_$/g, ""); // Remove leading or trailing underscores
      return `${baseId}_${idx}`;
    },
  },
};
</script>

<style>
.radio-group-fieldset {
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.radio-group-legend {
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  float: none;
  width: auto;
}

.multiselect-field-label {
  display: block;
  margin-bottom: 16px;
}

.multiselect-field-label-extended {
  margin-bottom: 8px;
}

.multiselect-field-description {
  display: block;
  margin-bottom: 8px;
}

.multiselect-field-disclaimer {
  display: block;
  margin-bottom: 16px;
}

.checkbox-columns {
  column-count: 2;
  column-gap: 20px;
}

/* For responsive design */
@media (max-width: 768px) {
  .checkbox-columns {
    column-count: 1;
  }
}

.checkbox-columns.one-column {
  column-count: 1;
}

/* Match FormRadioFieldComponent: checkbox on the right, label text to its left (RTL) */
.checkbox-columns.one-column .checkbox-option-label {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  margin-right: 0;
}

.checkbox-columns.many-items {
  column-count: 3;
}

.multiselect-option-wrap {
  display: block;
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
  margin-bottom: 10px;
  padding-right: 4px;
}

.checkbox-option-label {
  display: block;
  margin-bottom: 0;
  margin-right: 20px;
}

.checkbox-columns.one-column .checkbox-option-text {
  margin-right: 0;
}

.checkbox-option-text {
  margin-right: 4px;
}

.checkbox-option-label.checkbox-option-rich {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
}

.checkbox-columns.one-column .checkbox-option-label.checkbox-option-rich {
  align-items: flex-start;
}

.checkbox-option-detail {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.multiselect-option-description {
  display: block;
  margin-top: 2px;
}

.required {
  color: red;
  margin-left: 5px;
}

.nested-multiselect-fields {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  margin-right: 20px;
  margin-bottom: 2px;
  padding-right: 0;
}
</style>